import { SyntheticEvent, useCallback, useContext } from "react"
import {
  useMatchRoute,
  useNavigate,
  useParams,
  useRouter,
} from "@tanstack/react-router"

import { selectIsConnected, useDappSelector } from "#/redux-state"
import { partners, routes } from "#/shared/constants"
import { FlowContext } from "#/shared/context"

import { useDepositedBefore } from "./portal"

export const useNoAccessRoute = (): boolean => {
  const isConnected = useDappSelector(selectIsConnected)
  const depositedBefore = useDepositedBefore()

  return isConnected && depositedBefore
}

export const useDAppRoute = () => {
  const matchRoute = useMatchRoute()

  const dAppRoutes = Object.values(routes).filter(
    (route) =>
      ![routes.ABOUT, routes.REWARDS, routes.TERMS, routes.PRIVACY]
        .filter(Boolean)
        .includes(route),
  )

  const isDAppRoute = dAppRoutes.some((route) => matchRoute({ to: route }))

  return isDAppRoute
}

export const useBitcoinRoute = () => {
  const matchRoute = useMatchRoute()
  const isBitcoinRoute = !!matchRoute({ to: routes.HODL, fuzzy: true })

  return isBitcoinRoute
}

export const usePartnerRoute = () => {
  const matchRoute = useMatchRoute()
  const isPartnerRoute = matchRoute({ to: routes.HODL_WITH, fuzzy: true })

  return !!isPartnerRoute
}

export const useHodlRoute = () => {
  const matchRoute = useMatchRoute()
  const isPartnerRoute = usePartnerRoute()

  const isHodlPage =
    matchRoute({ to: routes.HODL, fuzzy: true }) ||
    matchRoute({ to: routes.HODL_ETHEREUM, fuzzy: true }) ||
    isPartnerRoute

  return isHodlPage
}

export const useLeaderboardRoute = () => {
  const matchRoute = useMatchRoute()
  const isLeaderboardRoute = matchRoute({ to: routes.LEADERBOARD })

  return isLeaderboardRoute
}

export const useLandingRoute = () => {
  const matchRoute = useMatchRoute()

  const isLandingRoute =
    matchRoute({ to: routes.BORROW_LANDING }) ||
    matchRoute({ to: routes.MUSD_LANDING }) ||
    matchRoute({ to: routes.GROWTH_LANDING }) ||
    matchRoute({ to: routes.LANDING })

  const isLandingHomeRoute = matchRoute({ to: routes.LANDING })

  const isLandingPageRoute =
    matchRoute({ to: routes.BORROW_LANDING }) ||
    matchRoute({ to: routes.GROWTH_LANDING }) ||
    matchRoute({ to: routes.MUSD_LANDING })

  return { isLandingRoute, isLandingHomeRoute, isLandingPageRoute }
}

export const useDepositRoute = () => {
  const matchRoute = useMatchRoute()
  const isDepositRoute = !!matchRoute({ to: routes.DEPOSIT })

  return isDepositRoute
}

export const usePartnerSlug = () => {
  const { partnerSlug } = useParams({ from: routes.HODL_WITH })

  return partnerSlug as keyof typeof partners
}

export const useHodlRedirect = () => {
  const { isBitcoinFlow } = useContext(FlowContext)
  const navigate = useNavigate()

  const navigateHandler = useCallback(
    (fromPage: string | undefined = undefined) => {
      const targetRoute = isBitcoinFlow ? routes.HODL : routes.HODL_ETHEREUM

      navigate({
        to: fromPage ? `${targetRoute}?from=${fromPage}` : targetRoute,
      })
    },
    [isBitcoinFlow, navigate],
  )

  return { redirect: navigateHandler }
}

export const useCurrentRoute = (href: string) => {
  const matchRoute = useMatchRoute()
  const isCurrentRoute = !!matchRoute({ to: href, fuzzy: true })

  return isCurrentRoute
}

export const useSinglePageRouterChange = () => {
  const { history } = useRouter()

  const changeRoute = useCallback(
    (
      event: Event | KeyboardEvent | SyntheticEvent<HTMLButtonElement, Event>,
      route: string | undefined,
    ) => {
      event.preventDefault()
      if (!route) return

      history.push(route)
    },
    [history],
  )

  return { changeRoute }
}

export const useHomeRoute = () => {
  const matchRoute = useMatchRoute()
  const isHomeRoute = !!matchRoute({ to: routes.HOME, fuzzy: true })

  return isHomeRoute
}

export const useExploreRoute = () => {
  const matchRoute = useMatchRoute()
  const isExploreRoute = !!matchRoute({ to: routes.EXPLORE })

  return isExploreRoute
}
